/**
 * Where an unread preview URL actually lives — outside React.
 *
 * The mint response is the only time a preview token exists anywhere:
 * `GET …/previews` returns ids and expiry, never tokens. So between
 * minting one and the reader copying it, the browser is holding something
 * irreplaceable, and anything that drops it has destroyed a link that
 * still exists, still bills a row, and can never be used.
 *
 * Five rounds of review found five ways to drop it: minting a second one
 * over it, the draft under it dying, revoking it from the list below it,
 * unmounting mid-request, and a site switch that changes a prop without
 * remounting. The first three are things a PERSON did, and a guard is the
 * right answer to those. The last two are things the CONTAINER did — and
 * that is the tell. While the token lived in `useState`, every way React
 * has of ending component state was a way to lose it, and no state
 * machine drawn on the token's own lifecycle can enumerate those: they
 * are not events in its life, they are events in its host's.
 *
 * So the token does not live in a component. It lives here, and the
 * components render from it. An unmount, a re-render, a route change, a
 * prop change, a parent that stops rendering the subtree — none of them
 * can reach this map, so none of them can lose anything. `mintHold` is
 * then left guarding the exits a PERSON can take, which is what a guard
 * is for.
 *
 * KEYED BY THE PREVIEWS PATH, so it carries client and site: a URL minted
 * for one site can never surface under another. `forgetAllMints` is
 * called on sign-out, because two people can share a tab and the map
 * would otherwise outlive the session that made it — `signOut` does not
 * always end in a page navigation (the Clerk redirect is conditional), so
 * memory is not reliably cleared for us.
 *
 * IN MEMORY, DELIBERATELY — not `sessionStorage`. This is a bearer token
 * for an unpublished draft. Surviving a reload is not worth writing it
 * somewhere a session restore could persist it to disk.
 *
 * An entry is kept until it is explicitly discarded or replaced, copied
 * or not. Clearing on copy would yank the panel out from under someone
 * who wants to copy it twice, and clearing on unmount is exactly the
 * lifecycle coupling this module exists to remove.
 */

/** What the mint response told us: the URL, its draft, and its own id. */
export interface Minted { url: string; draft_id: string; id: string }
/** …plus whether the reader has taken it, which is what unlocks the exits. */
export interface MintEntry { minted: Minted; copied: boolean }

const mints = new Map<string, MintEntry>();
const listeners = new Set<() => void>();
const emit = () => { for (const l of listeners) l(); };

export function subscribeMints(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => { listeners.delete(onChange); };
}

/**
 * The snapshot for `useSyncExternalStore`, which demands a STABLE
 * reference while nothing has changed — every mutator below therefore
 * replaces the entry object exactly once per real change, and returns
 * early when there is nothing to change. Building a fresh object here
 * would spin the render loop.
 */
export function readMint(key: string): MintEntry | undefined {
  return mints.get(key);
}

export function rememberMint(key: string, minted: Minted): void {
  mints.set(key, { minted, copied: false });
  emit();
}

export function markMintCopied(key: string): void {
  const e = mints.get(key);
  if (!e || e.copied) return;
  mints.set(key, { minted: e.minted, copied: true });
  emit();
}

export function forgetMint(key: string): void {
  if (mints.delete(key)) emit();
}

/** Sign-out. Another person may use this tab next. */
export function forgetAllMints(): void {
  if (mints.size === 0) return;
  mints.clear();
  emit();
}
